import {
  Box,
  Button,
  Container,
  createStyles,
  Group,
  Image,
  Stack,
  Text,
  ThemeIcon,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import React, {useState} from 'react';

import * as MfaDAO from 'auth/MfaDAO';
import {redirectTo} from 'BenchlingUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const useStyles = createStyles((theme) => ({
  card: {
    borderRadius: theme.radius.sm,
  },
  helpIcon: {
    backgroundColor: `${theme.colors.gray[4]} !important`,
  },
}));

function AuthCard({children}: {children: React.ReactNode}): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <Box id="auth" className="auth-v2" h="100%">
      <Box className="container">
        <Container className={cx('centered-box sign-in-v2', classes.card)}>{children}</Container>
      </Box>
    </Box>
  );
}

export default function VerifyMfaPage(): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(): Promise<void> {
    setError(null);
    setIsSubmitting(true);
    try {
      const {redirectUrl} = await MfaDAO.verifyMfaCode(code);
      redirectTo(redirectUrl);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch {
      setError("That code doesn't match. Please try again.");
      setIsSubmitting(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' && code.length === 6 && !isSubmitting) {
      handleSubmit();
    }
  }

  return (
    <AuthCard>
      <Stack spacing="md" align="center" {...getTestProps({component: 'VerifyMfaPage', element: 'root'})}>
        <a href="/" className="logo">
          <Image
            width={240}
            height={52}
            src="/static/img/bioblue_wordmark_500.png"
            className="brand-benchling-logo"
            alt="Benchling"
          />
        </a>
        <Title order={2} ta="center">
          Enter authenticator code
        </Title>
        <Text size="sm" c="dimmed" ta="center">
          Your account has two-factor authentication enabled.
          <br />
          Enter the rotating code from your authenticator app to log in
        </Text>
        <TextInput
          w={160}
          placeholder="000000"
          value={code}
          onChange={(e) => {
            setCode(e.currentTarget.value.replace(/\D/g, ''));
            setError(null);
          }}
          inputMode="numeric"
          onKeyDown={handleKeyDown}
          maxLength={6}
          disabled={isSubmitting}
          error={Boolean(error)}
          styles={{input: {textAlign: 'center', fontSize: 16}}}
          {...getTestProps({component: 'VerifyMfaPage', element: 'codeInput'})}
        />
        {error != null && (
          <Text size="sm" c="red" ta="center" mt={-8}>
            {error}
          </Text>
        )}
        <Button
          w="100%"
          size="md"
          onClick={handleSubmit}
          disabled={code.length !== 6 || isSubmitting}
          loading={isSubmitting}
          {...getTestProps({component: 'VerifyMfaPage', element: 'authenticateButton'})}
        >
          Authenticate
        </Button>
        <Group w="100%" position="apart">
          <Text size="xs" component="a" href="/signin" c="blue">
            Sign in to a different account
          </Text>
          <Tooltip label="Contact your administrator to reset your MFA configuration" withArrow={true}>
            <Text size="xs" c="dimmed" component="span" tabIndex={0}>
              <Group spacing={4} align="center" display="inline-flex">
                <ThemeIcon radius="xl" size="xs" className={classes.helpIcon}>
                  <BenchlingIcon icon="question" />
                </ThemeIcon>
                Can't sign in?
              </Group>
            </Text>
          </Tooltip>
        </Group>
      </Stack>
    </AuthCard>
  );
}
